import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputClass = "w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-yellow-400 focus:outline-none transition-colors";
const cardClass = "bg-gray-900 border border-yellow-400/20 rounded-2xl p-6";
const buttonClass = "bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg font-bold";
const secondaryButtonClass = "bg-gray-800 hover:bg-gray-700 text-yellow-400 px-4 py-2 rounded-lg font-bold border border-yellow-400/30";
const dangerButtonClass = "bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-sm font-bold";

const resolveProtectedUrl = (url) => {
  if (!url) return '';
  const resolved = /^https?:\/\//i.test(url) ? url : `${BACKEND_URL}${url.startsWith('/') ? '' : '/'}${url}`;
  if (!/\/api\/(files|employee)\//i.test(resolved)) return resolved;
  try {
    const token = localStorage.getItem('token');
    if (!token) return resolved;
    const separator = resolved.includes('?') ? '&' : '?';
    return `${resolved}${separator}access_token=${encodeURIComponent(token)}`;
  } catch (error) {
    return resolved;
  }
};

const SUB_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'announcements', label: 'Announcements' },
  { id: 'lectures', label: 'Lectures' },
  { id: 'materials', label: 'Course Materials' },
  { id: 'assignments', label: 'Assignments' },
  { id: 'quizzes', label: 'Quizzes' },
  { id: 'attendance', label: 'Attendance' },
  { id: 'students', label: 'Students' },
  { id: 'grades', label: 'Grades' },
  { id: 'schedule', label: 'Class Schedule' },
  { id: 'meetings', label: 'Meetings' },
  { id: 'discussions', label: 'Discussions' },
  { id: 'files', label: 'Course Files' },
  { id: 'settings', label: 'Settings' },
];

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
    <div className="bg-gray-900 rounded-lg max-w-2xl w-full border border-yellow-400 max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
        <h3 className="text-lg font-bold text-yellow-400">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-white text-xl leading-none">&times;</button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

const EmptyState = ({ title, message }) => (
  <div className={cardClass}>
    <h3 className="text-yellow-400 font-bold mb-1">{title}</h3>
    <p className="text-gray-400 text-sm">{message}</p>
  </div>
);

const TeacherCourseManagement = ({ courseId, courseTitle, initialSubTab = 'overview', highlightParams = {}, onBack, onOpenGlobalTab }) => {
  const [subTab, setSubTab] = useState(SUB_TABS.some((t) => t.id === initialSubTab) ? initialSubTab : 'overview');
  const [error, setError] = useState('');

  // Data state
  const [lectures, setLectures] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [discussions, setDiscussions] = useState([]);
  const [students, setStudents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loadedTabs, setLoadedTabs] = useState({});

  useEffect(() => {
    if (initialSubTab && SUB_TABS.some((t) => t.id === initialSubTab)) {
      setSubTab(initialSubTab);
    }
  }, [courseId, initialSubTab]);

  const safeCall = useCallback(async (fn) => {
    try {
      setError('');
      await fn();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Something went wrong. Please try again.');
    }
  }, []);

  const fetchLectures = useCallback(() => safeCall(async () => {
    const res = await axios.get(`${API}/teacher/courses/${courseId}/lectures`);
    setLectures(res.data.lectures || []);
  }), [courseId, safeCall]);

  const fetchMaterials = useCallback(() => safeCall(async () => {
    const res = await axios.get(`${API}/teacher/courses/${courseId}/materials`);
    setMaterials(res.data.materials || []);
  }), [courseId, safeCall]);

  const fetchAnnouncements = useCallback(() => safeCall(async () => {
    const res = await axios.get(`${API}/teacher/courses/${courseId}/announcements`);
    setAnnouncements(res.data.announcements || []);
  }), [courseId, safeCall]);

  const fetchDiscussions = useCallback(() => safeCall(async () => {
    const res = await axios.get(`${API}/courses/${courseId}/discussions`);
    setDiscussions(res.data.discussions || []);
  }), [courseId, safeCall]);

  const fetchStudents = useCallback(() => safeCall(async () => {
    const res = await axios.get(`${API}/teacher/courses/${courseId}/students`);
    setStudents(res.data.students || []);
  }), [courseId, safeCall]);

  const fetchAssignments = useCallback(() => safeCall(async () => {
    const res = await axios.get(`${API}/teacher/assignments`, { params: { course_id: courseId } });
    setAssignments(res.data.assignments || []);
  }), [courseId, safeCall]);

  const fetchClasses = useCallback(() => safeCall(async () => {
    const res = await axios.get(`${API}/teacher/classes`);
    setClasses((res.data.classes || []).filter((c) => c.course_id === courseId));
  }), [courseId, safeCall]);

  const fetchMeetings = useCallback(() => safeCall(async () => {
    const classRes = await axios.get(`${API}/teacher/classes`);
    const courseClassIds = new Set((classRes.data.classes || []).filter((c) => c.course_id === courseId).map((c) => c.id));
    const meetingsRes = await axios.get(`${API}/teacher/meetings`);
    setMeetings((meetingsRes.data.meetings || []).filter((m) => courseClassIds.has(m.class_id)));
  }), [courseId, safeCall]);

  useEffect(() => {
    if (!courseId || loadedTabs[subTab]) return;
    const loaders = {
      overview: async () => { await Promise.all([fetchLectures(), fetchMaterials(), fetchAnnouncements(), fetchStudents(), fetchAssignments()]); },
      announcements: fetchAnnouncements,
      lectures: fetchLectures,
      materials: fetchMaterials,
      assignments: fetchAssignments,
      attendance: fetchClasses,
      students: fetchStudents,
      schedule: fetchClasses,
      meetings: fetchMeetings,
      discussions: fetchDiscussions,
      files: fetchMaterials,
    };
    const loader = loaders[subTab];
    if (loader) {
      loader();
      setLoadedTabs((prev) => ({ ...prev, [subTab]: true }));
    }
  }, [subTab, courseId]);

  if (!courseId) {
    return (
      <EmptyState title="No course selected" message="Choose a course to manage from the Courses tab." />
    );
  }

  return (
    <section className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <button onClick={onBack} className="text-yellow-400 hover:text-yellow-300 text-sm font-medium mb-2">&larr; Back to Courses</button>
          <h2 className="text-2xl font-bold text-yellow-400">{courseTitle || 'Course Management'}</h2>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/40 border border-red-500 text-red-200 rounded-xl p-4 text-sm">{error}</div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-gray-800 pb-4">
        {SUB_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${subTab === t.id ? 'bg-yellow-500 text-black' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'overview' && (
        <OverviewTab
          lectures={lectures}
          materials={materials}
          announcements={announcements}
          students={students}
          assignments={assignments}
        />
      )}
      {subTab === 'announcements' && (
        <AnnouncementsTab courseId={courseId} announcements={announcements} refresh={fetchAnnouncements} setError={setError} highlightId={highlightParams.request_id} />
      )}
      {subTab === 'lectures' && (
        <LecturesTab courseId={courseId} lectures={lectures} refresh={fetchLectures} setError={setError} />
      )}
      {subTab === 'materials' && (
        <MaterialsTab courseId={courseId} materials={materials} refresh={fetchMaterials} setError={setError} />
      )}
      {subTab === 'assignments' && (
        <AssignmentsTab courseId={courseId} assignments={assignments} refresh={fetchAssignments} setError={setError} highlightId={highlightParams.assignment_id} />
      )}
      {subTab === 'quizzes' && (
        <EmptyState title="Quizzes" message="Quiz creation is coming soon. This tab is ready for future rollout." />
      )}
      {subTab === 'attendance' && (
        <AttendanceTab classes={classes} onOpenGlobalTab={onOpenGlobalTab} />
      )}
      {subTab === 'students' && (
        <StudentsTab students={students} highlightId={highlightParams.student_id} />
      )}
      {subTab === 'grades' && (
        <EmptyState title="Grades" message="A full gradebook view is coming soon. Use the Assignments tab to grade submissions today." />
      )}
      {subTab === 'schedule' && (
        <ScheduleTab classes={classes} onOpenGlobalTab={onOpenGlobalTab} />
      )}
      {subTab === 'meetings' && (
        <MeetingsTab meetings={meetings} onOpenGlobalTab={onOpenGlobalTab} />
      )}
      {subTab === 'discussions' && (
        <DiscussionsTab courseId={courseId} discussions={discussions} refresh={fetchDiscussions} setError={setError} highlightId={highlightParams.discussion_id} />
      )}
      {subTab === 'files' && (
        <FilesTab materials={materials} />
      )}
      {subTab === 'settings' && (
        <SettingsTab courseTitle={courseTitle} />
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------
const OverviewTab = ({ lectures, materials, announcements, students, assignments }) => {
  const pinned = announcements.filter((a) => a.is_pinned);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className={cardClass}><div className="text-gray-400 text-sm">Students</div><div className="text-yellow-400 text-2xl font-bold">{students.length}</div></div>
        <div className={cardClass}><div className="text-gray-400 text-sm">Lectures</div><div className="text-yellow-400 text-2xl font-bold">{lectures.length}</div></div>
        <div className={cardClass}><div className="text-gray-400 text-sm">Materials</div><div className="text-yellow-400 text-2xl font-bold">{materials.length}</div></div>
        <div className={cardClass}><div className="text-gray-400 text-sm">Assignments</div><div className="text-yellow-400 text-2xl font-bold">{assignments.length}</div></div>
        <div className={cardClass}><div className="text-gray-400 text-sm">Announcements</div><div className="text-yellow-400 text-2xl font-bold">{announcements.length}</div></div>
      </div>
      {pinned.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-yellow-400 font-bold">Pinned Announcements</h3>
          {pinned.map((a) => (
            <div key={a.id} className={cardClass}>
              <h4 className="text-yellow-400 font-bold mb-1">{a.title}</h4>
              <p className="text-gray-300 text-sm">{a.content}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Announcements
// ---------------------------------------------------------------------------
const AnnouncementsTab = ({ courseId, announcements, refresh, setError, highlightId }) => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', is_pinned: false, publish_at: '', expires_at: '' });

  const openCreate = () => { setEditing(null); setForm({ title: '', content: '', is_pinned: false, publish_at: '', expires_at: '' }); setShowForm(true); };
  const openEdit = (a) => { setEditing(a); setForm({ title: a.title, content: a.content, is_pinned: a.is_pinned, publish_at: a.publish_at || '', expires_at: a.expires_at || '' }); setShowForm(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, publish_at: form.publish_at || null, expires_at: form.expires_at || null };
      if (editing) {
        await axios.put(`${API}/teacher/announcements/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/teacher/courses/${courseId}/announcements`, payload);
      }
      setShowForm(false);
      refresh();
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'Failed to save announcement.');
    }
  };

  const togglePin = async (id) => { await axios.post(`${API}/teacher/announcements/${id}/pin`); refresh(); };
  const remove = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    await axios.delete(`${API}/teacher/announcements/${id}`);
    refresh();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><button onClick={openCreate} className={buttonClass}>+ New Announcement</button></div>
      {announcements.length === 0 && <EmptyState title="No announcements yet" message="Post an announcement to keep students informed." />}
      {announcements.map((a) => (
        <div key={a.id} className={`${cardClass} ${highlightId === a.id ? 'border-yellow-400' : ''}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-yellow-400 font-bold">{a.title}</h4>
                {a.is_pinned && <span className="text-xs bg-yellow-500 text-black px-2 py-0.5 rounded-full font-bold">Pinned</span>}
              </div>
              <p className="text-gray-300 text-sm whitespace-pre-wrap">{a.content}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => togglePin(a.id)} className={secondaryButtonClass}>{a.is_pinned ? 'Unpin' : 'Pin'}</button>
              <button onClick={() => openEdit(a)} className={secondaryButtonClass}>Edit</button>
              <button onClick={() => remove(a.id)} className={dangerButtonClass}>Delete</button>
            </div>
          </div>
        </div>
      ))}
      {showForm && (
        <Modal title={editing ? 'Edit Announcement' : 'New Announcement'} onClose={() => setShowForm(false)}>
          <form onSubmit={submit} className="space-y-4">
            <input required className={inputClass} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <textarea required className={`${inputClass} min-h-[120px]`} placeholder="Content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
            <label className="flex items-center gap-2 text-gray-300 text-sm">
              <input type="checkbox" checked={form.is_pinned} onChange={(e) => setForm({ ...form, is_pinned: e.target.checked })} /> Pin to top
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-gray-400 text-xs mb-1">Publish at (optional)</label>
                <input type="datetime-local" className={inputClass} value={form.publish_at} onChange={(e) => setForm({ ...form, publish_at: e.target.value })} />
              </div>
              <div>
                <label className="block text-gray-400 text-xs mb-1">Expires at (optional)</label>
                <input type="datetime-local" className={inputClass} value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)} className={secondaryButtonClass}>Cancel</button>
              <button type="submit" className={buttonClass}>{editing ? 'Save Changes' : 'Post Announcement'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Lectures
// ---------------------------------------------------------------------------
const LecturesTab = ({ courseId, lectures, refresh, setError }) => {
  const [showForm, setShowForm] = useState(false);
  const [mode, setMode] = useState('link');
  const [form, setForm] = useState({ title: '', resource_type: 'video', resource_url: '', description: '', status: 'draft' });
  const [file, setFile] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (mode === 'link') {
        await axios.post(`${API}/teacher/courses/${courseId}/lectures`, form);
      } else {
        if (!file) { setError('Please choose a file to upload.'); return; }
        const fd = new FormData();
        fd.append('title', form.title);
        fd.append('description', form.description || '');
        fd.append('resource_type', form.resource_type);
        fd.append('lecture_file', file);
        await axios.post(`${API}/teacher/courses/${courseId}/lectures/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setShowForm(false);
      setForm({ title: '', resource_type: 'video', resource_url: '', description: '', status: 'draft' });
      setFile(null);
      refresh();
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'Failed to save lecture.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><button onClick={() => setShowForm(true)} className={buttonClass}>+ Add Lecture</button></div>
      {lectures.length === 0 && <EmptyState title="No lectures yet" message="Add videos, PDFs, slides, documents, ZIPs, or external links." />}
      {lectures.map((l) => (
        <div key={l.id} className={cardClass}>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-yellow-400 font-bold">{l.title}</h4>
                <span className="text-xs bg-gray-800 text-gray-300 px-2 py-0.5 rounded-full capitalize">{l.resource_type}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${l.status === 'published' ? 'bg-green-800 text-green-200' : 'bg-gray-700 text-gray-300'}`}>{l.status}</span>
              </div>
              {l.description && <p className="text-gray-400 text-sm">{l.description}</p>}
              <a href={resolveProtectedUrl(l.resource_url)} target="_blank" rel="noreferrer" className="text-yellow-400 text-sm hover:underline">View resource</a>
            </div>
          </div>
        </div>
      ))}
      {showForm && (
        <Modal title="Add Lecture" onClose={() => setShowForm(false)}>
          <form onSubmit={submit} className="space-y-4">
            <div className="flex gap-2">
              <button type="button" onClick={() => setMode('link')} className={mode === 'link' ? buttonClass : secondaryButtonClass}>External Link</button>
              <button type="button" onClick={() => setMode('file')} className={mode === 'file' ? buttonClass : secondaryButtonClass}>Upload File</button>
            </div>
            <input required className={inputClass} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <select className={inputClass} value={form.resource_type} onChange={(e) => setForm({ ...form, resource_type: e.target.value })}>
              <option value="video">Video</option>
              <option value="pdf">PDF</option>
              <option value="document">Document (PPT/DOCX/ZIP/etc.)</option>
              <option value="assignment">Assignment</option>
              {mode === 'link' && <option value="external_link">External Link</option>}
            </select>
            {mode === 'link' ? (
              <input required className={inputClass} placeholder="Resource URL" value={form.resource_url} onChange={(e) => setForm({ ...form, resource_url: e.target.value })} />
            ) : (
              <input required type="file" accept=".mp4,.mov,.avi,.pdf,.doc,.docx,.ppt,.pptx,.zip,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files[0])} className={inputClass} />
            )}
            <textarea className={inputClass} placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            {mode === 'link' && (
              <select className={inputClass} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            )}
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)} className={secondaryButtonClass}>Cancel</button>
              <button type="submit" className={buttonClass}>Save Lecture</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Materials
// ---------------------------------------------------------------------------
const CATEGORY_OPTIONS = ['worksheet', 'practice', 'reading', 'reference', 'coding', 'project_template', 'resource'];

const MaterialsTab = ({ courseId, materials, refresh, setError }) => {
  const [showForm, setShowForm] = useState(false);
  const [mode, setMode] = useState('link');
  const [form, setForm] = useState({ title: '', description: '', category: 'resource', folder: 'General', resource_url: '' });
  const [file, setFile] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (mode === 'link') {
        await axios.post(`${API}/teacher/courses/${courseId}/materials`, form);
      } else {
        if (!file) { setError('Please choose a file to upload.'); return; }
        const fd = new FormData();
        fd.append('title', form.title);
        fd.append('description', form.description || '');
        fd.append('category', form.category);
        fd.append('folder', form.folder || 'General');
        fd.append('material_file', file);
        await axios.post(`${API}/teacher/courses/${courseId}/materials/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setShowForm(false);
      setForm({ title: '', description: '', category: 'resource', folder: 'General', resource_url: '' });
      setFile(null);
      refresh();
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'Failed to save material.');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this material?')) return;
    await axios.delete(`${API}/teacher/materials/${id}`);
    refresh();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><button onClick={() => setShowForm(true)} className={buttonClass}>+ Add Material</button></div>
      {materials.length === 0 && <EmptyState title="No course materials yet" message="Add worksheets, practice sets, references, or ZIP archives." />}
      {materials.map((m) => (
        <div key={m.id} className={cardClass}>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-yellow-400 font-bold">{m.title}</h4>
                <span className="text-xs bg-gray-800 text-gray-300 px-2 py-0.5 rounded-full capitalize">{m.category}</span>
                <span className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded-full">{m.folder}</span>
              </div>
              {m.description && <p className="text-gray-400 text-sm">{m.description}</p>}
              <a href={resolveProtectedUrl(m.resource_url)} target="_blank" rel="noreferrer" className="text-yellow-400 text-sm hover:underline">Open file</a>
            </div>
            <button onClick={() => remove(m.id)} className={dangerButtonClass}>Delete</button>
          </div>
        </div>
      ))}
      {showForm && (
        <Modal title="Add Material" onClose={() => setShowForm(false)}>
          <form onSubmit={submit} className="space-y-4">
            <div className="flex gap-2">
              <button type="button" onClick={() => setMode('link')} className={mode === 'link' ? buttonClass : secondaryButtonClass}>External Link</button>
              <button type="button" onClick={() => setMode('file')} className={mode === 'file' ? buttonClass : secondaryButtonClass}>Upload File</button>
            </div>
            <input required className={inputClass} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <select className={inputClass} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input className={inputClass} placeholder="Folder (e.g. Week 1)" value={form.folder} onChange={(e) => setForm({ ...form, folder: e.target.value })} />
            {mode === 'link' ? (
              <input required className={inputClass} placeholder="Resource URL" value={form.resource_url} onChange={(e) => setForm({ ...form, resource_url: e.target.value })} />
            ) : (
              <input required type="file" onChange={(e) => setFile(e.target.files[0])} className={inputClass} />
            )}
            <textarea className={inputClass} placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)} className={secondaryButtonClass}>Cancel</button>
              <button type="submit" className={buttonClass}>Save Material</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Assignments
// ---------------------------------------------------------------------------
const AssignmentsTab = ({ courseId, assignments, refresh, setError, highlightId }) => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', due_date: '', max_score: 100 });

  const openCreate = () => { setEditing(null); setForm({ title: '', description: '', due_date: '', max_score: 100 }); setShowForm(true); };
  const openEdit = (a) => { setEditing(a); setForm({ title: a.title, description: a.description, due_date: a.due_date || '', max_score: a.max_score }); setShowForm(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, due_date: form.due_date || null, max_score: Number(form.max_score) || 100 };
      if (editing) {
        await axios.put(`${API}/teacher/assignments/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/teacher/courses/${courseId}/assignments`, payload);
      }
      setShowForm(false);
      refresh();
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'Failed to save assignment.');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this assignment?')) return;
    await axios.delete(`${API}/teacher/assignments/${id}`);
    refresh();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><button onClick={openCreate} className={buttonClass}>+ New Assignment</button></div>
      {assignments.length === 0 && <EmptyState title="No assignments yet" message="Create an assignment for students in this course." />}
      {assignments.map((a) => (
        <div key={a.id} className={`${cardClass} ${highlightId === a.id ? 'border-yellow-400' : ''}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h4 className="text-yellow-400 font-bold mb-1">{a.title}</h4>
              <p className="text-gray-300 text-sm mb-1">{a.description}</p>
              <p className="text-gray-400 text-xs">Due: {a.due_date || 'No due date'} &bull; Max score: {a.max_score}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => openEdit(a)} className={secondaryButtonClass}>Edit</button>
              <button onClick={() => remove(a.id)} className={dangerButtonClass}>Delete</button>
            </div>
          </div>
        </div>
      ))}
      {showForm && (
        <Modal title={editing ? 'Edit Assignment' : 'New Assignment'} onClose={() => setShowForm(false)}>
          <form onSubmit={submit} className="space-y-4">
            <input required className={inputClass} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <textarea required className={`${inputClass} min-h-[100px]`} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-gray-400 text-xs mb-1">Due date (optional)</label>
                <input type="date" className={inputClass} value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
              <div>
                <label className="block text-gray-400 text-xs mb-1">Max score</label>
                <input type="number" min="0" className={inputClass} value={form.max_score} onChange={(e) => setForm({ ...form, max_score: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)} className={secondaryButtonClass}>Cancel</button>
              <button type="submit" className={buttonClass}>{editing ? 'Save Changes' : 'Create Assignment'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Attendance / Schedule / Meetings (read-focused, deep-link to existing tools)
// ---------------------------------------------------------------------------
const AttendanceTab = ({ classes, onOpenGlobalTab }) => (
  <div className="space-y-4">
    <div className={cardClass}>
      <p className="text-gray-300 text-sm">Attendance is recorded per class session. Use the full Attendance tools to mark attendance for a session below.</p>
      {onOpenGlobalTab && <button onClick={() => onOpenGlobalTab('classes')} className={`${secondaryButtonClass} mt-3`}>Open Attendance Tools</button>}
    </div>
    {classes.length === 0 && <EmptyState title="No classes scheduled" message="Classes for this course will appear here once scheduled." />}
    {classes.map((c) => (
      <div key={c.id} className={cardClass}>
        <h4 className="text-yellow-400 font-bold mb-1">{c.name || c.title || 'Class Session'}</h4>
        <p className="text-gray-400 text-sm">{c.date} &bull; {c.start_time} - {c.end_time} &bull; Status: {c.status}</p>
      </div>
    ))}
  </div>
);

const ScheduleTab = ({ classes, onOpenGlobalTab }) => (
  <div className="space-y-4">
    <div className={cardClass}>
      <p className="text-gray-300 text-sm">This is the class schedule for this course.</p>
      {onOpenGlobalTab && <button onClick={() => onOpenGlobalTab('classes')} className={`${secondaryButtonClass} mt-3`}>Manage Classes</button>}
    </div>
    {classes.length === 0 && <EmptyState title="No classes scheduled" message="Scheduled sessions for this course will appear here." />}
    {classes.map((c) => (
      <div key={c.id} className={cardClass}>
        <h4 className="text-yellow-400 font-bold mb-1">{c.name || c.title || 'Class Session'}</h4>
        <p className="text-gray-400 text-sm">{c.date} &bull; {c.start_time} - {c.end_time}</p>
        <p className="text-gray-400 text-xs mt-1">Students: {(c.student_ids || []).length}</p>
      </div>
    ))}
  </div>
);

const MeetingsTab = ({ meetings, onOpenGlobalTab }) => (
  <div className="space-y-4">
    <div className={cardClass}>
      <p className="text-gray-300 text-sm">Zoom / Google Meet sessions linked to classes in this course.</p>
      {onOpenGlobalTab && <button onClick={() => onOpenGlobalTab('meetings')} className={`${secondaryButtonClass} mt-3`}>Schedule a Meeting</button>}
    </div>
    {meetings.length === 0 && <EmptyState title="No meetings scheduled" message="Meetings scheduled for this course's classes will appear here." />}
    {meetings.map((m) => (
      <div key={m.id} className={cardClass}>
        <h4 className="text-yellow-400 font-bold mb-1">{m.title}</h4>
        <p className="text-gray-400 text-sm capitalize">{m.provider?.replace(/_/g, ' ')} &bull; {m.date} &bull; {m.start_time} - {m.end_time}</p>
        <a href={m.meeting_url} target="_blank" rel="noreferrer" className="text-yellow-400 text-sm hover:underline">Join link</a>
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Students
// ---------------------------------------------------------------------------
const StudentsTab = ({ students, highlightId }) => {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('name');

  const filtered = students
    .filter((s) => `${s.name} ${s.email}`.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortKey === 'attendance_rate' || sortKey === 'progress_percent') {
        return (b[sortKey] || 0) - (a[sortKey] || 0);
      }
      return (a[sortKey] || '').toString().localeCompare((b[sortKey] || '').toString());
    });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <input className={`${inputClass} max-w-xs`} placeholder="Search students..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className={`${inputClass} max-w-[200px]`} value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
          <option value="name">Sort by Name</option>
          <option value="attendance_rate">Sort by Attendance</option>
          <option value="progress_percent">Sort by Progress</option>
        </select>
      </div>
      {filtered.length === 0 && <EmptyState title="No students found" message="Enrolled students for this course will appear here." />}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map((s) => (
          <div key={s.id} className={`${cardClass} ${highlightId === s.id ? 'border-yellow-400' : ''}`}>
            <div className="flex items-center gap-3">
              {s.profile_photo_url ? (
                <img src={s.profile_photo_url} alt={s.name} className="w-12 h-12 rounded-full object-cover border border-yellow-400/40" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center text-yellow-400 font-bold">{(s.name || '?').charAt(0).toUpperCase()}</div>
              )}
              <div className="flex-1">
                <h4 className="text-yellow-400 font-bold">{s.name}</h4>
                <p className="text-gray-400 text-xs">{s.email}</p>
              </div>
              <a href={`mailto:${s.email}`} className={secondaryButtonClass}>Message</a>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3 text-center">
              <div><div className="text-gray-400 text-xs">Attendance</div><div className="text-yellow-400 font-bold">{s.attendance_rate}%</div></div>
              <div><div className="text-gray-400 text-xs">Progress</div><div className="text-yellow-400 font-bold">{s.progress_percent}%</div></div>
              <div><div className="text-gray-400 text-xs">Status</div><div className="text-yellow-400 font-bold capitalize">{s.status}</div></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Discussions
// ---------------------------------------------------------------------------
const DiscussionsTab = ({ courseId, discussions, refresh, setError, highlightId }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', body: '' });
  const [openThread, setOpenThread] = useState(highlightId || null);
  const [replies, setReplies] = useState({});
  const [replyText, setReplyText] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/courses/${courseId}/discussions`, form);
      setShowForm(false);
      setForm({ title: '', body: '' });
      refresh();
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'Failed to create discussion.');
    }
  };

  const loadReplies = async (discussionId) => {
    if (openThread === discussionId) { setOpenThread(null); return; }
    setOpenThread(discussionId);
    try {
      const res = await axios.get(`${API}/courses/${courseId}/discussions/${discussionId}/replies`);
      setReplies((prev) => ({ ...prev, [discussionId]: res.data.replies || [] }));
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'Failed to load replies.');
    }
  };

  const postReply = async (discussionId) => {
    if (!replyText.trim()) return;
    try {
      await axios.post(`${API}/courses/${courseId}/discussions/${discussionId}/replies`, { body: replyText });
      setReplyText('');
      const res = await axios.get(`${API}/courses/${courseId}/discussions/${discussionId}/replies`);
      setReplies((prev) => ({ ...prev, [discussionId]: res.data.replies || [] }));
      refresh();
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'Failed to post reply.');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this discussion thread?')) return;
    await axios.delete(`${API}/teacher/discussions/${id}`);
    refresh();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><button onClick={() => setShowForm(true)} className={buttonClass}>+ New Discussion</button></div>
      {discussions.length === 0 && <EmptyState title="No discussions yet" message="Start a discussion thread for this course." />}
      {discussions.map((d) => (
        <div key={d.id} className={`${cardClass} ${highlightId === d.id ? 'border-yellow-400' : ''}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 cursor-pointer" onClick={() => loadReplies(d.id)}>
              <h4 className="text-yellow-400 font-bold mb-1">{d.title}</h4>
              <p className="text-gray-300 text-sm">{d.body}</p>
              <p className="text-gray-400 text-xs mt-1">{d.author_name} &bull; {d.reply_count || 0} replies</p>
            </div>
            <button onClick={() => remove(d.id)} className={dangerButtonClass}>Delete</button>
          </div>
          {openThread === d.id && (
            <div className="mt-4 border-t border-gray-700 pt-4 space-y-3">
              {(replies[d.id] || []).map((r) => (
                <div key={r.id} className="bg-gray-800 rounded-lg p-3">
                  <p className="text-gray-300 text-sm">{r.body}</p>
                  <p className="text-gray-500 text-xs mt-1">{r.author_name}</p>
                </div>
              ))}
              <div className="flex gap-2">
                <input className={inputClass} placeholder="Write a reply..." value={replyText} onChange={(e) => setReplyText(e.target.value)} />
                <button onClick={() => postReply(d.id)} className={buttonClass}>Reply</button>
              </div>
            </div>
          )}
        </div>
      ))}
      {showForm && (
        <Modal title="New Discussion" onClose={() => setShowForm(false)}>
          <form onSubmit={submit} className="space-y-4">
            <input required className={inputClass} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <textarea required className={`${inputClass} min-h-[100px]`} placeholder="Body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)} className={secondaryButtonClass}>Cancel</button>
              <button type="submit" className={buttonClass}>Post</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Files (folder-organized view of materials)
// ---------------------------------------------------------------------------
const FilesTab = ({ materials }) => {
  const folders = materials.reduce((acc, m) => {
    const key = m.folder || 'General';
    acc[key] = acc[key] || [];
    acc[key].push(m);
    return acc;
  }, {});
  const folderNames = Object.keys(folders).sort();

  return (
    <div className="space-y-4">
      {folderNames.length === 0 && <EmptyState title="No files yet" message="Upload materials to organize them into folders here." />}
      {folderNames.map((folder) => (
        <div key={folder} className={cardClass}>
          <h4 className="text-yellow-400 font-bold mb-3">📁 {folder}</h4>
          <div className="space-y-2">
            {folders[folder].map((m) => (
              <a key={m.id} href={resolveProtectedUrl(m.resource_url)} target="_blank" rel="noreferrer" className="flex items-center justify-between bg-gray-800 rounded-lg px-3 py-2 hover:bg-gray-700 transition-colors">
                <span className="text-gray-200 text-sm">{m.title}</span>
                <span className="text-gray-400 text-xs capitalize">{m.category}</span>
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Settings (read-only; core edits remain admin-controlled)
// ---------------------------------------------------------------------------
const SettingsTab = ({ courseTitle }) => (
  <div className={cardClass}>
    <h4 className="text-yellow-400 font-bold mb-2">Course Settings</h4>
    <p className="text-gray-300 text-sm mb-2">Course: {courseTitle}</p>
    <p className="text-gray-400 text-sm">Core course details (title, category, creation, deletion) are managed by K.R.A administrators. Contact an admin to request changes.</p>
  </div>
);

export default TeacherCourseManagement;
